/**
 * Frontend Compatibility Service
 * 
 * This service ensures that existing frontend components continue to work
 * while new unified features are being introduced.
 * 
 * Key responsibilities:
 * 1. API endpoint compatibility
 * 2. Data format transformation
 * 3. Feature detection and graceful degradation
 * 4. Migration path support
 */

import { ApiService } from './api';

export interface CompatibilityOptions {
  useUnifiedApi?: boolean;
  legacyFormat?: boolean;
  featureFlags?: Record<string, boolean>;
}

export class CompatibilityService {
  private static instance: CompatibilityService;
  private options: CompatibilityOptions;

  private constructor(options: CompatibilityOptions = {}) {
    this.options = {
      useUnifiedApi: false, // Default to legacy for backward compatibility
      legacyFormat: true,
      featureFlags: {},
      ...options
    };
  }

  public static getInstance(options?: CompatibilityOptions): CompatibilityService {
    if (!CompatibilityService.instance) {
      CompatibilityService.instance = new CompatibilityService(options);
    }
    return CompatibilityService.instance;
  }

  /**
   * Update compatibility options
   */
  public updateOptions(options: Partial<CompatibilityOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Check if a feature is available
   */
  public isFeatureAvailable(feature: string): boolean {
    const defaultFeatures = {
      'unified-guest-management': false,
      'pin-authentication': true,
      'bulk-operations': false,
      'migration-tools': false,
      'enhanced-guest-table': false
    };

    return this.options.featureFlags?.[feature] ?? defaultFeatures[feature as keyof typeof defaultFeatures] ?? false;
  }

  /**
   * Get the appropriate API endpoint based on compatibility settings
   */
  public getApiEndpoint(operation: string): string {
    const endpointMappings = {
      // Guest management endpoints
      'create-guest': this.options.useUnifiedApi 
        ? '/api/unified-guest-management/guests'
        : '/admin/users/create',
      
      'list-guests': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests'
        : '/admin/guests',
      
      'update-guest': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests'
        : '/admin/guests',
      
      'delete-guest': '/admin/guests', // Same for both
      
      // PIN management endpoints
      'generate-pin': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests/{id}/pin'
        : '/auth/admin/generate-pin',
      
      'reset-pin': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests/{id}/pin'
        : '/auth/admin/reset-pin',
      
      'bulk-pin-operations': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests/bulk-operations'
        : '/auth/admin/bulk-generate-pins',
      
      // Migration endpoints
      'migrate-to-pin': this.options.useUnifiedApi
        ? '/api/unified-guest-management/guests/migrate-to-pin'
        : null // Not available in legacy
    };

    return endpointMappings[operation as keyof typeof endpointMappings] || operation;
  }

  /**
   * Transform guest data between legacy and unified formats
   */
  public transformGuestData(guest: any, toFormat: 'legacy' | 'unified'): any {
    if (!guest) return guest;

    if (toFormat === 'legacy') {
      return {
        id: guest.id,
        email: guest.email,
        full_name: guest.fullName || guest.full_name,
        phone_number: guest.phoneNumber || guest.phone_number,
        oauth_provider: guest.oauthProvider || guest.oauth_provider || 'cognito',
        status: guest.status,
        created_at: guest.createdAt || guest.created_at,
        updated_at: guest.updatedAt || guest.updated_at,
        
        // Enhanced fields (optional)
        pin_status: guest.pinStatus,
        auth_method: guest.authMethod,
        migration_status: guest.migrationStatus
      };
    } else {
      return {
        id: guest.id,
        email: guest.email,
        fullName: guest.full_name || guest.fullName,
        phoneNumber: guest.phone_number || guest.phoneNumber,
        oauthProvider: guest.oauth_provider || guest.oauthProvider,
        status: guest.status,
        createdAt: guest.created_at || guest.createdAt,
        updatedAt: guest.updated_at || guest.updatedAt,
        
        // Enhanced unified fields
        authMethod: guest.auth_method || guest.authMethod || 'password',
        pinStatus: guest.pin_status || guest.pinStatus || 'none',
        migrationStatus: guest.migration_status || guest.migrationStatus || 'pending'
      };
    }
  }

  /**
   * Create guest with compatibility layer
   */
  public async createGuest(guestData: any): Promise<any> {
    try {
      const endpoint = this.getApiEndpoint('create-guest');
      
      if (this.options.useUnifiedApi) {
        // Use new unified API
        const unifiedData = {
          email: guestData.email,
          fullName: guestData.fullName || guestData.full_name,
          phoneNumber: guestData.phoneNumber || guestData.phone_number,
          authMethod: guestData.authMethod || 'pin', // Default to PIN-first
          generatePin: guestData.generatePin ?? true,
          customPin: guestData.customPin,
          emailPin: guestData.emailPin ?? true,
          // Legacy compatibility
          temporaryPassword: guestData.temporaryPassword,
          oauthProvider: guestData.oauthProvider
        };

        const response = await ApiService.post(endpoint, unifiedData);
        
        // Transform response to expected format if needed
        if (this.options.legacyFormat && response.success) {
          response.data.guest = this.transformGuestData(response.data.guest, 'legacy');
        }
        
        return response;
      } else {
        // Use legacy API
        const legacyData = {
          email: guestData.email,
          fullName: guestData.fullName || guestData.full_name,
          phoneNumber: guestData.phoneNumber || guestData.phone_number,
          temporaryPassword: guestData.temporaryPassword,
          addToGuestList: guestData.addToGuestList ?? true
        };

        return await ApiService.post(endpoint, legacyData);
      }
    } catch (error) {
      console.error('Guest creation failed:', error);
      throw error;
    }
  }

  /**
   * Get guests with compatibility layer
   */
  public async getGuests(filters: any = {}): Promise<any> {
    try {
      const endpoint = this.getApiEndpoint('list-guests');
      
      if (this.options.useUnifiedApi) {
        // Use new unified API with enhanced filtering
        const params = {
          includePin: 'true',
          migrationStatus: filters.migrationStatus,
          authMethod: filters.authMethod,
          pinStatus: filters.pinStatus,
          status: filters.status,
          search: filters.search
        };

        const response = await ApiService.get(endpoint, params);
        
        // Transform response format if needed
        if (this.options.legacyFormat && response.success) {
          response.data.guests = response.data.guests.map((guest: any) => 
            this.transformGuestData(guest, 'legacy')
          );
        }
        
        return response;
      } else {
        // Use legacy API
        const params = {
          status: filters.status,
          search: filters.search
        };

        const response = await ApiService.get(endpoint, params);
        
        // Enhance legacy response with default unified fields
        if (response.success && response.data.guests) {
          response.data.guests = response.data.guests.map((guest: any) => ({
            ...guest,
            authMethod: 'password', // Default for legacy
            pinStatus: 'none',
            migrationStatus: 'pending'
          }));
        }
        
        return response;
      }
    } catch (error) {
      console.error('Guest list fetch failed:', error);
      throw error;
    }
  }

  /**
   * PIN operations with compatibility layer
   */
  public async performPinOperation(guestId: string, operation: string, options: any = {}): Promise<any> {
    try {
      if (this.options.useUnifiedApi) {
        // Use new unified PIN API
        const endpoint = this.getApiEndpoint('generate-pin').replace('{id}', guestId);
        
        const requestData = {
          action: operation, // 'generate', 'reset', 'unlock', 'set'
          customPin: options.customPin,
          emailPin: options.emailPin ?? true,
          reason: options.reason
        };

        return await ApiService.put(endpoint, requestData);
      } else {
        // Use legacy PIN API
        let endpoint: string;
        let requestData: any;

        switch (operation) {
          case 'generate':
          case 'reset':
            endpoint = '/auth/admin/generate-pin';
            requestData = { guestId };
            break;
          case 'set':
            endpoint = '/auth/admin/set-pin';
            requestData = { guestId, pin: options.customPin };
            break;
          case 'unlock':
            endpoint = '/auth/admin/unlock-account';
            requestData = { guestId };
            break;
          default:
            throw new Error(`Unsupported PIN operation: ${operation}`);
        }

        return await ApiService.post(endpoint, requestData);
      }
    } catch (error) {
      console.error('PIN operation failed:', error);
      throw error;
    }
  }

  /**
   * Bulk operations with compatibility layer
   */
  public async performBulkOperation(guestIds: string[], operation: string, options: any = {}): Promise<any> {
    try {
      if (this.options.useUnifiedApi && this.isFeatureAvailable('bulk-operations')) {
        // Use new unified bulk API
        const endpoint = this.getApiEndpoint('bulk-pin-operations');
        
        const requestData = {
          guestIds,
          operation,
          options
        };

        return await ApiService.post(endpoint, requestData);
      } else {
        // Fall back to individual operations or legacy bulk API
        if (operation === 'generate-pins') {
          const endpoint = '/auth/admin/bulk-generate-pins';
          return await ApiService.post(endpoint, { guestIds });
        } else if (operation === 'email-pins') {
          const endpoint = '/auth/admin/bulk-email-pins';
          return await ApiService.post(endpoint, { 
            guestIds, 
            partyName: options.partyName 
          });
        } else {
          // Perform individual operations
          const results = {
            processed: guestIds.length,
            successful: 0,
            failed: 0,
            results: [] as any[]
          };

          for (const guestId of guestIds) {
            try {
              const result = await this.performPinOperation(guestId, operation.replace('-pins', ''), options);
              results.successful++;
              results.results.push({
                guestId,
                success: true,
                ...result.data
              });
            } catch (error) {
              results.failed++;
              results.results.push({
                guestId,
                success: false,
                error: (error as Error).message
              });
            }
          }

          return {
            success: true,
            data: results
          };
        }
      }
    } catch (error) {
      console.error('Bulk operation failed:', error);
      throw error;
    }
  }

  /**
   * Check if migration is needed for a guest
   */
  public async checkMigrationNeeded(guestId: string): Promise<any> {
    try {
      // This would typically call a migration status endpoint
      // For now, return a mock response
      return {
        needsMigration: false,
        currentAuthMethod: 'password',
        hasPin: false,
        hasPassword: true
      };
    } catch (error) {
      console.error('Migration check failed:', error);
      return {
        needsMigration: false,
        currentAuthMethod: 'unknown',
        hasPin: false,
        hasPassword: false
      };
    }
  }

  /**
   * Handle feature degradation gracefully
   */
  public handleFeatureDegradation(feature: string): {
    available: boolean;
    message: string;
    fallback?: string;
  } {
    const degradationInfo = {
      'unified-guest-management': {
        available: false,
        message: 'Unified guest management not available, using legacy interface',
        fallback: 'legacy-guest-management'
      },
      'bulk-operations': {
        available: false,
        message: 'Bulk operations not available, process guests individually',
        fallback: 'individual-operations'
      },
      'migration-tools': {
        available: false,
        message: 'Migration tools not available, manual migration required',
        fallback: 'manual-migration'
      }
    };

    return degradationInfo[feature as keyof typeof degradationInfo] || {
      available: false,
      message: `Feature ${feature} not available`,
      fallback: undefined
    };
  }

  /**
   * Get compatibility status for monitoring
   */
  public getCompatibilityStatus(): {
    apiMode: 'legacy' | 'unified' | 'hybrid';
    availableFeatures: string[];
    degradedFeatures: string[];
    recommendations: string[];
  } {
    const features = [
      'unified-guest-management',
      'pin-authentication',
      'bulk-operations',
      'migration-tools',
      'enhanced-guest-table'
    ];

    const availableFeatures = features.filter(f => this.isFeatureAvailable(f));
    const degradedFeatures = features.filter(f => !this.isFeatureAvailable(f));

    const apiMode = this.options.useUnifiedApi 
      ? (availableFeatures.length > 0 ? 'unified' : 'hybrid')
      : 'legacy';

    const recommendations = [];
    if (degradedFeatures.length > 0) {
      recommendations.push('Consider enabling unified API for enhanced features');
    }
    if (!this.isFeatureAvailable('bulk-operations')) {
      recommendations.push('Bulk operations will be processed individually');
    }

    return {
      apiMode,
      availableFeatures,
      degradedFeatures,
      recommendations
    };
  }
}